import {
  createContext,
  useContext,
  useMemo,
  type PropsWithChildren,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { getTenantSlugFromPath } from "~/utils/tenant";
import { publicApiClient } from "~/lib/orpc-client";

type TenantData = {
  slug: string | null;
  orgId: string | null;
  name: string | null;
  displayName: string | null;
  type: string | null;
  allowedFlows: string[];
  allowedEmailDomains: string[];
  branding: {
    logoUrl?: string;
    primaryColor?: string;
  };
  isUniversity: boolean;
  isLoading: boolean;
  isTenantPortal: boolean;
  error: string | null;
};

const defaultTenant: TenantData = {
  slug: null,
  orgId: null,
  name: null,
  displayName: null,
  type: null,
  allowedFlows: [],
  allowedEmailDomains: [],
  branding: {},
  isUniversity: false,
  isLoading: false,
  isTenantPortal: false,
  error: null,
};

const TenantContext = createContext<TenantData>(defaultTenant);

export function TenantProvider({ children }: PropsWithChildren) {
  const slug = getTenantSlugFromPath();
  const { data, isLoading, error } = useQuery({
    queryKey: ["tenant", slug],
    queryFn: () =>
      publicApiClient.organizations.resolveBySlug({ slug: slug! }),
    enabled: !!slug,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const tenant = useMemo<TenantData>(() => {
    if (!slug) return defaultTenant;

    if (isLoading) {
      return { ...defaultTenant, slug, isLoading: true, isTenantPortal: true };
    }

    if (error || !data) {
      return {
        ...defaultTenant,
        slug,
        isTenantPortal: true,
        error: error instanceof Error ? error.message : "Failed to resolve portal",
      };
    }

    return {
      slug,
      orgId: data.orgId,
      name: data.name,
      displayName: data.displayName,
      type: data.type ?? null,
      allowedFlows: data.allowedFlows,
      allowedEmailDomains: data.allowedEmailDomains,
      branding: data.branding,
      isUniversity:
        data.type === "university" || data.allowedFlows.includes("THESIS"),
      isLoading: false,
      isTenantPortal: true,
      error: null,
    };
  }, [slug, isLoading, error, data]);

  return (
    <TenantContext.Provider value={tenant}>{children}</TenantContext.Provider>
  );
}

export function useTenant() {
  return useContext(TenantContext);
}
